import { Injectable } from '@angular/core';
import { Action, NgxsAfterBootstrap, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, patch, updateItem } from '@ngxs/store/operators';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { EntityTypeEnum } from '../../enums/entity-type.enum';
import { ICustodyDTOIn, ICustodyDTOOut } from '../../interfaces/garde.interface';
import { IAdmin, IAdminDTOIn } from '../../interfaces/users/administrator.interface';
import { IContact, IContactDTOIn } from '../../interfaces/users/contact.interface';
import { IEmployee, IEmployeeDTOIn } from '../../interfaces/users/employee.interface';
import { IParent, IParentDTOIn } from '../../interfaces/users/parent.interface';
import { IRelative, IRelativeDTOIn } from '../../interfaces/users/relative.interface';
import { IStudent, IStudentDTOIn } from '../../interfaces/users/student.interface';
import {
    ILightUser,
    ILightUserDTO,
    IUser,
    IUserDTOIn,
    IUsersFromBack,
    IUsersLight,
    IUsersLightFromBack,
} from '../../interfaces/users/user.interface';
import { GardesService } from '../../services/garde.service';
import { MappersService } from '../../services/mappers.service';
import { UsersService } from '../../services/users.service';
import { PatchMyUser } from '../actions/auth.action';
import { DeleteCachedClasses } from '../actions/classes.action';
import {
    GetClassTypesEnum,
    GetContactTypesEnum,
    GetCostsRepartitionEnum,
    GetCreditTypesEnum,
    GetCustodyTypesEnum,
    GetDebitTypesEnum,
    GetEducationLevelsEnum,
    GetEducationTypesEnum,
    GetGendersEnum,
    GetRateTypesEnum,
    GetRelativeTypesEnum,
    GetRolesEnum,
} from '../actions/enums.action';
import { SetError, SetIsOwnError } from '../actions/errors.action';
import { DeleteCachedGroups } from '../actions/groups.action';
import { SetRepertoire } from '../actions/repertoire.action';
import {
    CreateNewAdministrator,
    CreateNewContact,
    CreateNewEmployee,
    CreateNewParent,
    CreateNewRelative,
    CreateNewStudent,
    DeleteAdministrator,
    DeleteCachedUsers,
    DeleteContact,
    DeleteEmployee,
    DeleteParent,
    DeleteRelative,
    DeleteStudent,
    DeleteUserToEdit,
    DeleteUserToView,
    DisableUser,
    EnableUser,
    GetAllContacts,
    GetAllEmployees,
    GetAllParents,
    GetAllStudents,
    GetAllUsers,
    GetAllUsersLight,
    GetCustodies,
    GetParentInCharge,
    GetSortedStudentNursery,
    GetUserById,
    GetUserRelatives,
    PatchAdministrator,
    PatchContact,
    PatchEmployee,
    PatchMyAccount,
    PatchParent,
    PatchRelative,
    PatchStudent,
    ResetCustody,
    ResetImpersonatedUser,
    ResetStudentIdOfCurrentWaitingCustody,
    ResetWaitingCustody,
    SaveCustody,
    SetIsImpersonated,
    SetStudentIdOfCurrentWaitingCustody,
    UpdateCustody,
} from '../actions/users.action';
import { GetAllClasses } from './../actions/classes.action';
import { GetAllGroups } from './../actions/groups.action';
import { AuthState } from './auth.state';

export interface UsersStateModel {
    users: IUser[];
    administrators: IAdmin[];
    employees: IEmployee[];
    parents: IParent[];
    students: IStudent[];
    relatives: IRelative[];
    contacts: IContact[];
    userToEdit: IUser;
    userToView: IUser;
    custodies: ICustodyDTOIn;
    waitingCustody: ICustodyDTOOut[];
    studentIdOfCurrentWaitingCustody: number;
    sortedStudentNursery: IStudent[];
    timeoutCache: ReturnType<typeof setTimeout>;
    usersLight: IUsersLight;
    parentInChargeId: number;
    isImpersonatedUserId: number;
    impersonatedUserRepertory: {
        id: number;
        usersLight: IUsersLight;
    };
}

@State<UsersStateModel>({
    name: 'users',
    defaults: {
        users: [],
        administrators: [],
        employees: [],
        parents: [],
        students: [],
        relatives: [],
        contacts: [],
        userToEdit: null,
        userToView: null,
        custodies: null,
        waitingCustody: [],
        studentIdOfCurrentWaitingCustody: null,
        sortedStudentNursery: [],
        timeoutCache: null,
        usersLight: null,
        parentInChargeId: null,
        isImpersonatedUserId: null,
        impersonatedUserRepertory: {
            id: null,
            usersLight: null,
        },
    },
})
@Injectable()
export class UsersState implements NgxsAfterBootstrap {
    constructor(
        private usersService: UsersService,
        private mapperService: MappersService,
        private store: Store,
        private gardeService: GardesService
    ) {}

    public ngxsAfterBootstrap(ctx: StateContext<UsersStateModel>): void {
        ctx.dispatch([
            new GetRolesEnum(),
            new GetGendersEnum(),
            new GetEducationLevelsEnum(),
            new GetEducationTypesEnum(),
            new GetCostsRepartitionEnum(),
            new GetClassTypesEnum(),
            new GetRateTypesEnum(),
            new GetCustodyTypesEnum(),
            new GetRelativeTypesEnum(),
            new GetContactTypesEnum(),
            new GetCreditTypesEnum(),
            new GetDebitTypesEnum(),
        ]);
    }

    @Selector()
    public static getAllUsersLight(state: UsersStateModel): IUsersLight {
        if (
            state.isImpersonatedUserId &&
            state.isImpersonatedUserId === state.impersonatedUserRepertory?.id &&
            state.impersonatedUserRepertory?.usersLight
        ) {
            return state.impersonatedUserRepertory?.usersLight;
        }
        if (!state.isImpersonatedUserId && state.usersLight) {
            return state.usersLight;
        }
        return null;
    }

    @Selector()
    public static isImpersonatedUserId(state: UsersStateModel): number {
        return state.isImpersonatedUserId;
    }

    @Selector()
    public static getAllUsers(state: UsersStateModel): IUser[] {
        return state.users;
    }

    @Selector()
    public static getAllEmployees(state: UsersStateModel): IEmployee[] {
        return state.employees;
    }

    @Selector()
    public static getAllParents(state: UsersStateModel): IParent[] {
        return state.parents;
    }

    @Selector()
    public static getAllStudents(state: UsersStateModel): IStudent[] {
        return state.students;
    }

    @Selector()
    public static getRelatives(state: UsersStateModel): IRelative[] {
        return state.relatives;
    }

    @Selector()
    public static getContacts(state: UsersStateModel): IContact[] {
        return state.contacts;
    }

    @Selector()
    public static userToEdit(state: UsersStateModel): IUser {
        return state.userToEdit;
    }

    @Selector()
    public static userToView(state: UsersStateModel): IUser {
        return state.userToView;
    }

    @Selector()
    public static getCustodies(state: UsersStateModel): ICustodyDTOIn {
        return state.custodies;
    }

    @Selector()
    public static getWaitingCustodies(state: UsersStateModel): ICustodyDTOOut[] {
        return state.waitingCustody;
    }

    @Selector()
    public static getSortedStudentsNursery(state: UsersStateModel): IStudent[] {
        return state.sortedStudentNursery;
    }

    @Selector()
    public static parentsForCustody(state: UsersStateModel): ILightUser[] {
        return state.isImpersonatedUserId ? state.impersonatedUserRepertory?.usersLight?.parents : state.usersLight?.parents;
    }

    @Selector()
    public static getParentInChargeId(state: UsersStateModel): number {
        return state.parentInChargeId;
    }

    @Selector()
    public static getStudentIdOfWaitingCustodies(state: UsersStateModel): number {
        return state.studentIdOfCurrentWaitingCustody;
    }

    // READ
    @Action(GetAllUsersLight)
    public getAllUsersLight(ctx: StateContext<UsersStateModel>, action: GetAllUsersLight): Observable<IUsersLightFromBack> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);

        const state: UsersStateModel = ctx.getState();

        if (!action.forceRefresh) {
            // si impersonné et déjà userlight
            if (
                state.isImpersonatedUserId &&
                state.isImpersonatedUserId === state.impersonatedUserRepertory?.id &&
                state.impersonatedUserRepertory?.usersLight
            ) {
                return null;
            }
            // si pas impersonné et déjà userlight
            if (!state.isImpersonatedUserId && state.usersLight) {
                return null;
            }
        }


        return this.usersService.getAllUsersLight(selectedUser).pipe(
            tap((result: IUsersLightFromBack) => {
                if (result) {
                    if (state.timeoutCache) {
                        clearTimeout(state.timeoutCache);
                    }
                    const usersLight: IUsersLight = {
                        administrators: result.administrators.map((user: ILightUserDTO) =>
                            this.mapperService.fromLightUserDTOToLightUser(user)
                        ),
                        employees: result.employees.map((user: ILightUserDTO) => this.mapperService.fromLightUserDTOToLightUser(user)),
                        parents: result.parents.map((user: ILightUserDTO) => this.mapperService.fromLightUserDTOToLightUser(user)),
                        students: result.students.map((user: ILightUserDTO) => this.mapperService.fromLightUserDTOToLightUser(user)),
                        relatives: result.relatives.map((user: ILightUserDTO) => this.mapperService.fromLightUserDTOToLightUser(user)),
                        contacts: result?.contacts?.map((user: ILightUserDTO) => this.mapperService.fromLightUserDTOToLightUser(user)),
                    };
                    if (state.isImpersonatedUserId) {
                        ctx.patchState({
                            impersonatedUserRepertory: {
                                ...state.impersonatedUserRepertory,
                                id: state.isImpersonatedUserId,
                                usersLight,
                            },
                        });
                    } else {
                        ctx.patchState({
                            usersLight,
                            timeoutCache: setTimeout(() => {
                                ctx.dispatch(new DeleteCachedUsers());
                            }, 43200000),
                        });
                    }
                    ctx.dispatch(new SetRepertoire());
                }
            })
        );
    }

    @Action(GetAllUsers)
    public getAllUsers(ctx: StateContext<UsersStateModel>): Observable<IUsersFromBack> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);

        const state: UsersStateModel = ctx.getState();

        if (state.users.length) {
            return null;
        }

        return this.usersService.getAllUsers(selectedUser).pipe(
            tap((result: IUsersFromBack) => {
                if (result) {
                    if (state.timeoutCache) {
                        clearTimeout(state.timeoutCache);
                    }

                    ctx.patchState({
                        users: this.usersPileUp(result),
                        administrators: result.administrators.map((user: IAdminDTOIn) => this.mapperService.fromIAdminDTOInToIAdmin(user)),
                        employees: result.employees.map((user: IUserDTOIn) =>
                            this.mapperService.castIUserToIEmployee(this.mapperService.fromIUserDTOInToIUser(user))
                        ),
                        parents: result.parents.map((user: IUserDTOIn) =>
                            this.mapperService.castIUserToIParent(this.mapperService.fromIUserDTOInToIUser(user))
                        ),
                        students: result.students
                            .map((user: IUserDTOIn) =>
                                this.mapperService.castIUserToIStudent(this.mapperService.fromIUserDTOInToIUser(user))
                            )
                            .sort((a: IStudent, b: IStudent) =>
                                (a.firstname + ' ' + a.lastname).localeCompare(b.firstname + ' ' + b.lastname)
                            ),
                        relatives: result.relatives.map((user: IUserDTOIn) =>
                            this.mapperService.castIUserToIRelative(this.mapperService.fromIUserDTOInToIUser(user))
                        ),
                        contacts: result.contacts.map((user: IUserDTOIn) =>
                            this.mapperService.castIUserToIContact(this.mapperService.fromIUserDTOInToIUser(user))
                        ),
                        timeoutCache: setTimeout(() => {
                            ctx.dispatch(new DeleteCachedUsers());
                        }, 43200000),
                    });
                }
            })
        );
    }

    @Action(GetAllEmployees)
    public getAllEmployees(ctx: StateContext<UsersStateModel>): Observable<IEmployee[]> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);

        if (ctx.getState().employees.length) {
            return null;
        }
        return this.usersService.getAllEmployees(selectedUser).pipe(
            tap((result: IEmployee[]) => {
                if (result) {
                    ctx.patchState({
                        employees: result,
                    });
                }
            })
        );
    }

    @Action(GetAllParents)
    public getAllParents(ctx: StateContext<UsersStateModel>): Observable<IParent[]> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);

        if (ctx.getState().parents.length) {
            return null;
        }
        return this.usersService.getAllParents(selectedUser).pipe(
            tap((result: IParent[]) => {
                if (result) {
                    ctx.patchState({
                        parents: result,
                    });
                }
            })
        );
    }

    @Action(GetAllStudents)
    public getAllStudents(ctx: StateContext<UsersStateModel>): Observable<IStudent[]> | null {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);

        if (connectedUser.type === EntityTypeEnum.EMPLOYEE) {
            return this.usersService.getAllStudents(null).pipe(
                tap((result: IStudent[]) => {
                    if (result) {
                        ctx.patchState({
                            students: result.sort((a: IStudent, b: IStudent) =>
                                (a.firstname + ' ' + a.lastname).localeCompare(b.firstname + ' ' + b.lastname)
                            ),
                        });
                    }
                })
            );
        }

        if (connectedUser.isAdmin) {
            return null;
        }
        return this.usersService.getAllStudents(selectedUser).pipe(
            tap((result: IStudent[]) => {
                if (result) {
                    ctx.patchState({
                        students: result.sort((a: IStudent, b: IStudent) =>
                            (a.firstname + ' ' + a.lastname).localeCompare(b.firstname + ' ' + b.lastname)
                        ),
                    });
                }
            })
        );
    }

    @Action(GetAllContacts)
    public getAllContacts(ctx: StateContext<UsersStateModel>): Observable<IContact[]> | null {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
        if (connectedUser.isAdmin || ctx.getState().contacts.length) {
            return null;
        }
        return this.usersService.getAllContacts(selectedUser).pipe(
            tap((result: IContact[]) => {
                if (result) {
                    ctx.patchState({
                        contacts: result,
                    });
                }
            })
        );
    }

    @Action(GetUserRelatives)
    public getUserRelatives(ctx: StateContext<UsersStateModel>): Observable<IRelative[]> {
        const selectedUser: number | null = this.store.selectSnapshot(AuthState.getStudentIdSelected);

        if (ctx.getState().relatives.length) {
            return null;
        }
        return this.usersService.getUserRelatives(selectedUser).pipe(
            tap((result: IRelative[]) => {
                if (result) {
                    ctx.patchState({
                        relatives: result,
                    });
                }
            })
        );
    }

    @Action(GetUserById)
    public setUserToEdit(ctx: StateContext<UsersStateModel>, action: GetUserById): Observable<IUser> | void {
        const state: UsersStateModel = ctx.getState();
        const userFound: IUser = state.users.find((user: IUser) => user.id === action.id && user.type === action.userType);
        if (!userFound) {
            switch (action.userType) {
                case EntityTypeEnum.EMPLOYEE:
                    return this.usersService.getEmployeeById(action.id).pipe(
                        tap((result: IUser) => {
                            if (result) {
                                ctx.patchState({
                                    userToEdit: action.forRepertoire ? null : result,
                                    userToView: action.forRepertoire ? result : null,
                                });
                            }
                        })
                    );
                case EntityTypeEnum.PARENT:
                    return this.usersService.getParentById(action.id).pipe(
                        tap((result: IUser) => {
                            if (result) {
                                ctx.patchState({
                                    userToEdit: action.forRepertoire ? null : result,
                                    userToView: action.forRepertoire ? result : null,
                                });
                            }
                        })
                    );
                case EntityTypeEnum.STUDENT:
                    return this.usersService.getStudentById(action.id).pipe(
                        tap((result: IUser) => {
                            if (result) {
                                ctx.patchState({
                                    userToEdit: action.forRepertoire ? null : result,
                                    userToView: action.forRepertoire ? result : null,
                                });
                            }
                        })
                    );
                case EntityTypeEnum.RELATIVE:
                    return this.usersService.getRelativeById(action.id).pipe(
                        tap((result: IUser) => {
                            if (result) {
                                ctx.patchState({
                                    userToEdit: action.forRepertoire ? null : result,
                                    userToView: action.forRepertoire ? result : null,
                                });
                            }
                        })
                    );
                case EntityTypeEnum.CONTACT:
                    return this.usersService.getContactById(action.id).pipe(
                        tap((result: IUser) => {
                            if (result) {
                                ctx.patchState({
                                    userToEdit: action.forRepertoire ? null : result,
                                    userToView: action.forRepertoire ? result : null,
                                });
                            }
                        })
                    );
                case EntityTypeEnum.ADMINISTRATOR:
                    return this.usersService.getAdministratorById(action.id).pipe(
                        tap((result: IUser) => {
                            if (result) {
                                ctx.patchState({
                                    userToEdit: action.forRepertoire ? null : result,
                                    userToView: action.forRepertoire ? result : null,
                                });
                            }
                        })
                    );
            }
        } else {
            ctx.patchState({
                userToEdit: action.forRepertoire ? null : userFound,
                userToView: action.forRepertoire ? userFound : null,
            });
        }
    }

    @Action(GetSortedStudentNursery)
    public getSortedStudentNursery(ctx: StateContext<UsersStateModel>): Observable<IStudent[]> {
        if (ctx.getState().sortedStudentNursery.length) {
            return null;
        }
        return this.usersService.getSortedStudentsPointings().pipe(
            tap((result: IStudent[]) => {
                ctx.patchState({
                    sortedStudentNursery: result,
                });
            })
        );
    }

    // CREATE
    @Action(CreateNewEmployee)
    public createNewEmployee(ctx: StateContext<UsersStateModel>, action: CreateNewEmployee): Observable<IEmployee> {
        return this.usersService.createEmployee(action.newEmployee).pipe(
            tap((result: IEmployee) => {
                ctx.setState(
                    patch({
                        userToEdit: null,
                        users: append([result] as IUser[]),
                        employees: append([result]),
                        usersLight: patch({
                            employees: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                        }),
                    })
                );
            })
        );
    }

    @Action(CreateNewAdministrator)
    public createNewAdministrator(ctx: StateContext<UsersStateModel>, action: CreateNewAdministrator): Observable<IAdmin> {
        return this.usersService.createAdministrator(action.newAdministrator).pipe(
            tap((result: IAdmin) => {
                ctx.setState(
                    patch({
                        userToEdit: null,
                        users: append([result] as IUser[]),
                        administrators: append([result]),
                        usersLight: patch({
                            administrators: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                        }),
                    })
                );
            })
        );
    }

    @Action(CreateNewParent)
    public createNewParent(ctx: StateContext<UsersStateModel>, action: CreateNewParent): Observable<IParent> {
        return this.usersService.createParent(action.newParent).pipe(
            tap((result: IParent) => {
                ctx.setState(
                    patch({
                        userToEdit: null,
                        users: append([result] as IUser[]),
                        parents: append([result]),
                        usersLight: patch({
                            parents: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                        }),
                    })
                );
            })
        );
    }

    @Action(CreateNewStudent)
    public createNewStudent(ctx: StateContext<UsersStateModel>, action: CreateNewStudent): Observable<IStudent> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.createStudent(action.newStudent).pipe(
            tap((result: IStudent) => {
                ctx.setState(
                    patch({
                        userToEdit: null,
                        users: append([result] as IUser[]),
                        students: append([result]),
                        usersLight: patch({
                            students: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                        }),
                    })
                );
                if (result.id && result.settings.parent_divorced) {
                    this.checkCustody(result, state, ctx);
                }
            })
        );
    }

    @Action(CreateNewRelative)
    public createNewRelative(ctx: StateContext<UsersStateModel>, action: CreateNewRelative): Observable<IRelative> {
        return this.usersService.createRelative(action.newRelative).pipe(
            tap((result: IRelative) => {
                ctx.setState(
                    patch({
                        userToEdit: null,
                        users: append([result] as IUser[]),
                        relatives: append([result]),
                    })
                );
                if (ctx.getState().isImpersonatedUserId) {
                    ctx.setState(
                        patch({
                            impersonatedUserRepertory: patch({
                                usersLight: patch({
                                    relatives: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                                }),
                            }),
                        })
                    );
                } else {
                    ctx.setState(
                        patch({
                            usersLight: patch({
                                relatives: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                            }),
                        })
                    );
                }
            })
        );
    }

    @Action(CreateNewContact)
    public createNewContact(ctx: StateContext<UsersStateModel>, action: CreateNewContact): Observable<IContact> {
        return this.usersService.createContact(action.newContact).pipe(
            tap((result: IContact) => {
                ctx.setState(
                    patch({
                        userToEdit: null,
                        users: append([result] as IUser[]),
                        contacts: append([result]),
                        usersLight: patch({
                            contacts: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                        }),
                    })
                );
            })
        );
    }

    // UPDATE
    @Action(PatchMyAccount)
    public updateMyAccount(ctx: StateContext<UsersStateModel>, action: PatchMyAccount): Observable<IUser> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.patchMyAccount(action.myUser).pipe(
            tap(
                (result: IUser) => {
                    if (action.userType === EntityTypeEnum.PARENT) {
                        ctx.setState(
                            patch({
                                users: updateItem<IUser>((user: IUser) => user.id === result.id, result as IUser),
                                parents: updateItem<IParent>(
                                    (parent: IParent) => parent.id === result.id,
                                    this.mapperService.castIUserToIParent(result)
                                ),
                                usersLight: patch({
                                    parents: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                                }),
                            })
                        );
                    } else if (action.userType === EntityTypeEnum.EMPLOYEE) {
                        ctx.setState(
                            patch({
                                users: updateItem<IUser>((user: IUser) => user.id === result.id, result as IUser),
                                employees: updateItem<IEmployee>(
                                    (employee: IEmployee) => employee.id === result.id,
                                    this.mapperService.castIUserToIEmployee(result)
                                ),
                                usersLight: patch({
                                    employees: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                                }),
                            })
                        );
                    } else if (action.userType === EntityTypeEnum.STUDENT) {
                        ctx.setState(
                            patch({
                                users: updateItem<IUser>((user: IUser) => user.id === result.id, result as IUser),
                                students: updateItem<IStudent>(
                                    (student: IStudent) => student.id === result.id,
                                    this.mapperService.castIUserToIStudent(result)
                                ),
                                usersLight: patch({
                                    students: append([this.mapperService.castIUserToILightUser(result as IUser)]),
                                }),
                            })
                        );
                        this.checkCustody(result as IStudent, state, ctx);
                    }
                    this.store.dispatch(new PatchMyUser(result));
                },
                (error: any) => {
                    this.store.dispatch(new SetIsOwnError()).subscribe(() => {
                        this.store.dispatch(new SetError(error));
                    });
                }
            )
        );
    }

    @Action(PatchEmployee)
    public updateEmployee(ctx: StateContext<UsersStateModel>, action: PatchEmployee): Observable<IEmployee> {
        const employeeIdToEdit: number = action.employee.employee.id;
        const state: UsersStateModel = ctx.getState();
        return this.usersService.patchEmployee(action.employee, employeeIdToEdit).pipe(
            tap((result: IEmployee) => {
                ctx.setState(
                    patch({
                        users: updateItem<IUser>((user: IUser) => user.id === employeeIdToEdit, result as IUser),
                        employees: updateItem<IEmployee>((employee: IEmployee) => employee.id === employeeIdToEdit, result),
                    })
                );
                if (ctx.getState().isImpersonatedUserId) {
                    ctx.setState(
                        patch({
                            impersonatedUserRepertory: patch({
                                usersLight: patch({
                                    employees: updateItem<ILightUser>(
                                        (userLight: ILightUser) => userLight.id === employeeIdToEdit,
                                        this.mapperService.castIUserToILightUser(result as IUser)
                                    ),
                                }),
                            }),
                        })
                    );
                } else if (state.usersLight?.employees?.length) {
                    ctx.setState(
                        patch({
                            usersLight: patch({
                                employees: updateItem<ILightUser>(
                                    (userLight: ILightUser) => userLight.id === employeeIdToEdit,
                                    this.mapperService.castIUserToILightUser(result as IUser)
                                ),
                            }),
                        })
                    );
                }
            })
        );
    }

    @Action(PatchAdministrator)
    public updateAdministrator(ctx: StateContext<UsersStateModel>, action: PatchAdministrator): Observable<IAdmin> {
        const administratorIdToEdit: number = action.administrator.administrator.id;
        return this.usersService.patchAdministrator(action.administrator, administratorIdToEdit).pipe(
            tap((result: IAdmin) => {
                ctx.setState(
                    patch({
                        users: updateItem<IUser>((user: IUser) => user.id === administratorIdToEdit, result as IUser),
                        administrators: updateItem<IAdmin>((administrator: IAdmin) => administrator.id === administratorIdToEdit, result),
                        usersLight: patch({
                            administrators: updateItem<ILightUser>(
                                (userLight: ILightUser) => userLight.id === administratorIdToEdit,
                                this.mapperService.castIUserToILightUser(result as IUser)
                            ),
                        }),
                    })
                );
            })
        );
    }

    @Action(PatchParent)
    public updateParent(ctx: StateContext<UsersStateModel>, action: PatchParent): Observable<IParent> {
        const parentIdToEdit: number = action.parent.parent.id;
        const state: UsersStateModel = ctx.getState();
        return this.usersService.patchParent(action.parent, parentIdToEdit).pipe(
            tap((result: IParent) => {
                ctx.setState(
                    patch({
                        users: updateItem<IUser>((user: IUser) => user.id === parentIdToEdit, result as IUser),
                        parents: updateItem<IParent>((parent: IParent) => parent.id === parentIdToEdit, result),
                    })
                );
                if (state.isImpersonatedUserId) {
                    ctx.setState(
                        patch({
                            impersonatedUserRepertory: patch({
                                usersLight: patch({
                                    parents: updateItem<ILightUser>(
                                        (userLight: ILightUser) => userLight.id === parentIdToEdit,
                                        this.mapperService.castIUserToILightUser(result as IUser)
                                    ),
                                }),
                            }),
                        })
                    );
                } else if (state.usersLight?.parents?.length) {
                    ctx.setState(
                        patch({
                            usersLight: patch({
                                parents: updateItem<ILightUser>(
                                    (userLight: ILightUser) => userLight.id === parentIdToEdit,
                                    this.mapperService.castIUserToILightUser(result as IUser)
                                ),
                            }),
                        })
                    );
                }
            })
        );
    }

    @Action(PatchStudent)
    public updateStudent(ctx: StateContext<UsersStateModel>, action: PatchStudent): Observable<IStudent> {
        const studentIdToEdit: number = action.student.student.id;
        const state: UsersStateModel = ctx.getState();
        return this.usersService.patchStudent(action.student, studentIdToEdit).pipe(
            tap((result: IStudent) => {
                ctx.setState(
                    patch({
                        users: updateItem<IUser>((user: IUser) => user.id === studentIdToEdit, result as IUser),
                        students: updateItem<IStudent>((student: IStudent) => student.id === studentIdToEdit, result),
                    })
                );
                if (ctx.getState().isImpersonatedUserId) {
                    ctx.setState(
                        patch({
                            impersonatedUserRepertory: patch({
                                usersLight: patch({
                                    students: updateItem<ILightUser>(
                                        (userLight: ILightUser) => userLight.id === studentIdToEdit,
                                        this.mapperService.castIUserToILightUser(result as IUser)
                                    ),
                                }),
                            }),
                        })
                    );
                } else if (state.usersLight?.students?.length) {
                    ctx.setState(
                        patch({
                            usersLight: patch({
                                students: updateItem<ILightUser>(
                                    (userLight: ILightUser) => userLight.id === studentIdToEdit,
                                    this.mapperService.castIUserToILightUser(result as IUser)
                                ),
                            }),
                        })
                    );
                }
                this.checkCustody(result, state, ctx);
            })
        );
    }

    @Action(PatchRelative)
    public updateRelative(ctx: StateContext<UsersStateModel>, action: PatchRelative): Observable<IRelative> {
        const relativeIdToEdit: number = action.relative.relative.id;
        const state: UsersStateModel = ctx.getState();
        return this.usersService.patchRelative(action.relative, relativeIdToEdit).pipe(
            tap((result: IRelative) => {
                ctx.setState(
                    patch({
                        users: updateItem<IUser>((user: IUser) => user.id === relativeIdToEdit, result as IUser),
                        relatives: updateItem<IRelative>((relative: IRelative) => relative.id === relativeIdToEdit, result),
                    })
                );
                if (ctx.getState().isImpersonatedUserId) {
                    ctx.setState(
                        patch({
                            impersonatedUserRepertory: patch({
                                usersLight: patch({
                                    relatives: updateItem<ILightUser>(
                                        (userLight: ILightUser) => userLight.id === relativeIdToEdit,
                                        this.mapperService.castIUserToILightUser(result as IUser)
                                    ),
                                }),
                            }),
                        })
                    );
                } else if (state.usersLight?.relatives?.length) {
                    ctx.setState(
                        patch({
                            usersLight: patch({
                                relatives: updateItem<ILightUser>(
                                    (userLight: ILightUser) => userLight.id === relativeIdToEdit,
                                    this.mapperService.castIUserToILightUser(result as IUser)
                                ),
                            }),
                        })
                    );
                }
            })
        );
    }

    @Action(PatchContact)
    public updateContact(ctx: StateContext<UsersStateModel>, action: PatchContact): Observable<IContact> {
        const contactIdToEdit: number = action.contact.contact.id;
        const state: UsersStateModel = ctx.getState();
        return this.usersService.patchContact(action.contact, contactIdToEdit).pipe(
            tap((result: IContact) => {
                ctx.setState(
                    patch({
                        users: updateItem<IUser>((user: IUser) => user.id === contactIdToEdit, result as IUser),
                        contacts: updateItem<IContact>((contact: IContact) => contact.id === contactIdToEdit, result),
                    })
                );
                if (ctx.getState().isImpersonatedUserId) {
                    ctx.setState(
                        patch({
                            impersonatedUserRepertory: patch({
                                usersLight: patch({
                                    contacts: updateItem<ILightUser>(
                                        (userLight: ILightUser) => userLight.id === contactIdToEdit,
                                        this.mapperService.castIUserToILightUser(result as IUser)
                                    ),
                                }),
                            }),
                        })
                    );
                } else if (state.usersLight?.contacts?.length) {
                    ctx.setState(
                        patch({
                            usersLight: patch({
                                contacts: updateItem<ILightUser>(
                                    (userLight: ILightUser) => userLight.id === contactIdToEdit,
                                    this.mapperService.castIUserToILightUser(result as IUser)
                                ),
                            }),
                        })
                    );
                }
            })
        );
    }

    // DELETE
    @Action(DeleteAdministrator)
    public deleteAdministrator(ctx: StateContext<UsersStateModel>, action: DeleteAdministrator): Observable<void> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.deleteAdministrator(action.administratorId).pipe(
            tap(() => {
                ctx.patchState({
                    administrators: state.administrators.filter((admin: IAdmin) => admin.id !== action.administratorId),
                    users: state.users.filter((admin: IUser) => admin.id !== action.administratorId),
                });
                ctx.setState(
                    patch({
                        usersLight: patch({
                            administrators: state.usersLight.administrators.filter(
                                (admin: ILightUser) => admin.id !== action.administratorId
                            ),
                        }),
                    })
                );
            })
        );
    }

    @Action(DeleteEmployee)
    public deleteEmployee(ctx: StateContext<UsersStateModel>, action: DeleteEmployee): Observable<void> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.deleteEmployee(action.employeeId).pipe(
            tap(() => {
                ctx.patchState({
                    employees: ctx.getState().employees.filter((employee: IEmployee) => employee.id !== action.employeeId),
                    users: state.users.filter((employee: IUser) => employee.id !== action.employeeId),
                });
                ctx.setState(
                    patch({
                        usersLight: patch({
                            employees: state.usersLight.employees.filter((employee: ILightUser) => employee.id !== action.employeeId),
                        }),
                    })
                );
            })
        );
    }

    @Action(DeleteParent)
    public deleteParent(ctx: StateContext<UsersStateModel>, action: DeleteParent): Observable<void> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.deleteParent(action.parentId).pipe(
            tap(() => {
                ctx.patchState({
                    parents: ctx.getState().parents.filter((parent: IParent) => parent.id !== action.parentId),
                    users: state.users.filter((parent: IUser) => parent.id !== action.parentId),
                });
                ctx.setState(
                    patch({
                        usersLight: patch({
                            parents: state.usersLight.parents.filter((parent: ILightUser) => parent.id !== action.parentId),
                        }),
                    })
                );
            })
        );
    }

    @Action(DeleteStudent)
    public deleteStudent(ctx: StateContext<UsersStateModel>, action: DeleteStudent): Observable<void> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.deleteStudent(action.studentId).pipe(
            tap(() => {
                ctx.patchState({
                    students: ctx.getState().students.filter((student: IStudent) => student.id !== action.studentId),
                    users: state.users.filter((student: IUser) => student.id !== action.studentId),
                });
                ctx.setState(
                    patch({
                        usersLight: patch({
                            students: state.usersLight.students.filter((student: ILightUser) => student.id !== action.studentId),
                        }),
                    })
                );
            })
        );
    }

    @Action(DeleteRelative)
    public deleteRelative(ctx: StateContext<UsersStateModel>, action: DeleteRelative): Observable<void> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.deleteRelative(action.relativeId).pipe(
            tap(() => {
                ctx.patchState({
                    relatives: ctx.getState().relatives.filter((relative: IRelative) => relative.id !== action.relativeId),
                    users: state.users.filter((relative: IUser) => relative.id !== action.relativeId),
                });
                ctx.setState(
                    patch({
                        usersLight: patch({
                            relatives: state.usersLight.relatives.filter((relative: ILightUser) => relative.id !== action.relativeId),
                        }),
                    })
                );
            })
        );
    }

    @Action(DeleteContact)
    public deleteContact(ctx: StateContext<UsersStateModel>, action: DeleteContact): Observable<void> {
        const state: UsersStateModel = ctx.getState();
        return this.usersService.deleteContact(action.contactId).pipe(
            tap(() => {
                ctx.patchState({
                    contacts: ctx.getState().contacts.filter((contact: IContact) => contact.id !== action.contactId),
                    users: state.users.filter((contact: IUser) => contact.id !== action.contactId),
                });
                ctx.setState(
                    patch({
                        usersLight: patch({
                            contacts: state.usersLight.contacts.filter((contact: ILightUser) => contact.id !== action.contactId),
                        }),
                    })
                );
            })
        );
    }

    // DELETE -> ENABLE & DISABLE
    @Action(EnableUser)
    public enableUser(ctx: StateContext<UsersStateModel>, action: EnableUser): Observable<IUser> {
        return this.usersService.enableUser(action.userId).pipe(
            tap((result: IUser) => {
                if (result.isActive === true) {
                    this.updateEnableAndDisableUser(ctx, result);
                }
            })
        );
    }

    @Action(DisableUser)
    public disableUser(ctx: StateContext<UsersStateModel>, action: DisableUser): Observable<IUser> {
        return this.usersService.disableUser(action.userId).pipe(
            tap((result: IUser) => {
                if (result.isActive === false) {
                    this.updateEnableAndDisableUser(ctx, result);
                }
            })
        );
    }

    // CUSTODIES
    @Action(GetCustodies)
    public getCustodies(ctx: StateContext<UsersStateModel>, action: GetCustodies): Observable<ICustodyDTOIn> {
        return this.gardeService.getCustodies(action.studentId).pipe(
            tap((result: ICustodyDTOIn) => {
                if (result) {
                    ctx.patchState({
                        custodies: result,
                    });
                }
            })
        );
    }

    @Action(SaveCustody)
    public saveCustody(ctx: StateContext<UsersStateModel>, action: SaveCustody): void {
        const state: UsersStateModel = ctx.getState();
        ctx.patchState({
            waitingCustody: [...state.waitingCustody, action.custody],
            studentIdOfCurrentWaitingCustody: action.studentId,
        });
    }

    @Action(UpdateCustody)
    public updateCustody(ctx: StateContext<UsersStateModel>, action: UpdateCustody): void {
        const state: UsersStateModel = ctx.getState();
        state.waitingCustody.forEach((c: ICustodyDTOOut) => {
            this.gardeService.updateCustodies(c, action.studentId, action.custodyId).subscribe((result: ICustodyDTOIn) => {
                if (result) {
                    ctx.patchState({
                        custodies: result,
                    });
                }
            });
        });
        ctx.patchState({
            waitingCustody: [],
        });
    }

    @Action(ResetCustody)
    public resetCustody(ctx: StateContext<UsersStateModel>): void {
        ctx.patchState({
            custodies: null,
            studentIdOfCurrentWaitingCustody: null,
        });
    }

    @Action(ResetWaitingCustody)
    public resetWaitingCustody(ctx: StateContext<UsersStateModel>): void {
        ctx.patchState({
            waitingCustody: [],
            studentIdOfCurrentWaitingCustody: null,
        });
    }

    @Action(SetStudentIdOfCurrentWaitingCustody)
    public setStudentIdOfCurrentWaitingCustody(ctx: StateContext<UsersStateModel>, action: SetStudentIdOfCurrentWaitingCustody): void {
        ctx.patchState({
            studentIdOfCurrentWaitingCustody: action.studentId,
        });
    }

    @Action(ResetStudentIdOfCurrentWaitingCustody)
    public resetStudentIdOfCurrentWaitingCustody(ctx: StateContext<UsersStateModel>): void {
        ctx.patchState({
            studentIdOfCurrentWaitingCustody: null,
        });
    }

    @Action(GetParentInCharge)
    public getParentInCharge(ctx: StateContext<UsersStateModel>, action: GetParentInCharge): Observable<number> {
        return this.usersService.getParentInCharge(action.studentId, action.date ? action.date : null, action.period ? action.period : null).pipe(
            tap((result: number) => {
                if (result) {
                    ctx.patchState({
                        parentInChargeId: result,
                    });
                }
            })
        );
    }

    // UTILS
    @Action(DeleteUserToEdit)
    public deleteUserToEdit(ctx: StateContext<UsersStateModel>): void {
        ctx.patchState({
            userToEdit: null,
        });
    }

    @Action(DeleteCachedUsers)
    public deleteCachedUsers(ctx: StateContext<UsersStateModel>): void {
        const state: UsersStateModel = ctx.getState();
        if (state.timeoutCache) {
            clearTimeout(state.timeoutCache);
        }
        ctx.patchState({
            users: [],
            administrators: [],
            employees: [],
            parents: [],
            students: [],
            relatives: [],
            contacts: [],
            sortedStudentNursery: [],
            timeoutCache: null,
            usersLight: null,
            custodies: null,
            impersonatedUserRepertory: null,
        });
        ctx.dispatch([new DeleteCachedClasses(), new DeleteCachedGroups()]);
    }

    @Action(ResetImpersonatedUser)
    public resetImpersonatedUser(ctx: StateContext<UsersStateModel>, action: ResetImpersonatedUser): void {
        const state: UsersStateModel = ctx.getState();
        if (action.newUserId !== state.impersonatedUserRepertory?.id) {
            if (state.timeoutCache) {
                clearTimeout(state.timeoutCache);
            }
            ctx.patchState({ sortedStudentNursery: [], timeoutCache: null, custodies: null });
            ctx.patchState({
                isImpersonatedUserId: null,
                impersonatedUserRepertory: {
                    id: null,
                    usersLight: null,
                },
            });
        }
        ctx.dispatch([new DeleteCachedClasses(), new DeleteCachedGroups()]);
        ctx.dispatch([new GetAllGroups(), new GetAllClasses()]);
    }

    @Action(DeleteUserToView)
    public deleteUserToView(ctx: StateContext<UsersStateModel>): void {
        ctx.patchState({
            userToView: null,
        });
    }

    @Action(SetIsImpersonated)
    public setIsImpersonated(ctx: StateContext<UsersStateModel>, action: SetIsImpersonated): void {
        ctx.patchState({
            isImpersonatedUserId: action.isImpersonated,
        });
    }

    private checkCustody(result: IStudent, state: UsersStateModel, ctx: StateContext<UsersStateModel>): void {
        if (state.waitingCustody.length && result.id && result.settings.student_custody_id) {
            // TODO - A checker, je ne vois pas de cas qui nécessite un tableau de waiting custodies
            // car on sauvegarde uniqument la derniere sauvegarde du calendrier de garde

            // state.waitingCustody.forEach((c: ICustodyDTOOut) => {
            //     this.gardeService.updateCustodies(c, result.id, result.settings.student_custody_id).subscribe(
            //         (v: ICustodyDTOIn) => {
            //             if (v) {
            //                 ctx.patchState({
            //                     custodies: v,
            //                 });
            //             }
            //         },
            //         (e: any) => {
            //             console.log(e);
            //         }
            //     );
            // });

            this.gardeService
                .updateCustodies(state.waitingCustody[state.waitingCustody.length - 1], result.id, result.settings.student_custody_id)
                .subscribe(
                    (v: ICustodyDTOIn) => {
                        if (v) {
                            ctx.patchState({
                                custodies: v,
                            });
                        }
                    },
                    (e: any) => {
                        console.log(e);
                    }
                );
        }
    }

    private updateEnableAndDisableUser(ctx: StateContext<UsersStateModel>, result: IUser): void {
        ctx.setState(
            patch({
                users: updateItem<IUser>((user: IUser) => user.id === result.id, result),
            })
        );
        if (result.type === EntityTypeEnum.EMPLOYEE) {
            ctx.setState(
                patch({
                    employees: updateItem<IEmployee>(
                        (user: IEmployee) => user.id === result.id,
                        this.mapperService.castIUserToIEmployee(result)
                    ),
                    usersLight: patch({
                        employees: updateItem<ILightUser>(
                            (userLight: ILightUser) => userLight.id === result.id,
                            this.mapperService.castIUserToILightUser(result as IUser)
                        ),
                    }),
                })
            );
        } else if (result.type === EntityTypeEnum.PARENT) {
            ctx.setState(
                patch({
                    parents: updateItem<IParent>((user: IParent) => user.id === result.id, this.mapperService.castIUserToIParent(result)),
                    usersLight: patch({
                        parents: updateItem<ILightUser>(
                            (userLight: ILightUser) => userLight.id === result.id,
                            this.mapperService.castIUserToILightUser(result as IUser)
                        ),
                    }),
                })
            );
        } else if (result.type === EntityTypeEnum.STUDENT) {
            ctx.setState(
                patch({
                    students: updateItem<IStudent>(
                        (user: IStudent) => user.id === result.id,
                        this.mapperService.castIUserToIStudent(result)
                    ),
                    usersLight: patch({
                        students: updateItem<ILightUser>(
                            (userLight: ILightUser) => userLight.id === result.id,
                            this.mapperService.castIUserToILightUser(result as IUser)
                        ),
                    }),
                })
            );
        } else if (result.type === EntityTypeEnum.ADMINISTRATOR) {
            ctx.setState(
                patch({
                    administrators: updateItem<IAdmin>(
                        (user: IAdmin) => user.id === result.id,
                        this.mapperService.castIUserToIAdmin(result)
                    ),
                    usersLight: patch({
                        administrators: updateItem<ILightUser>(
                            (userLight: ILightUser) => userLight.id === result.id,
                            this.mapperService.castIUserToILightUser(result as IUser)
                        ),
                    }),
                })
            );
        }
    }

    private usersPileUp(usersContainer: IUsersFromBack): IUser[] {
        let users: IUser[] = [];
        users = [
            ...users,
            ...usersContainer.administrators.map((user: IAdminDTOIn) => this.mapperService.fromIUserDTOInToIUser(user as IUserDTOIn)),
            ...usersContainer.employees.map((user: IEmployeeDTOIn) => this.mapperService.fromIUserDTOInToIUser(user as IUserDTOIn)),
            ...usersContainer.parents.map((user: IParentDTOIn) => this.mapperService.fromIUserDTOInToIUser(user as IUserDTOIn)),
            ...usersContainer.students.map((user: IStudentDTOIn) => this.mapperService.fromIUserDTOInToIUser(user as IUserDTOIn)),
            ...usersContainer?.relatives.map((user: IRelativeDTOIn) => this.mapperService.fromIUserDTOInToIUser(user as IUserDTOIn)),
            ...usersContainer?.contacts?.map((user: IContactDTOIn) => this.mapperService.fromIUserDTOInToIUser(user as IUserDTOIn)),
        ];
        return users;
    }
}
